import { useRef, useState, type FormEvent } from "react";
import { DictationTextarea } from "@/components/ui/DictationTextarea";
import type { Prestation } from "@shared/schema";

interface InternalState {
  clientName: string;
  clientEmail: string;
  description: string;
  amount: string; // text in the input, parsed on submit
  invoicedAt: string;
  dueAt: string;
  notes: string;
}

function toState(p?: Partial<Prestation>): InternalState {
  return {
    clientName: p?.clientName ?? "",
    clientEmail: p?.clientEmail ?? "",
    description: p?.description ?? "",
    amount: p?.amount != null ? String(p.amount) : "",
    invoicedAt: p?.invoicedAt ?? new Date().toISOString().slice(0, 10),
    dueAt: p?.dueAt ?? "",
    notes: p?.notes ?? "",
  };
}

export interface PrestationFormValues {
  clientName: string;
  clientEmail: string | null;
  description: string | null;
  amount: number;
  invoicedAt: string;
  dueAt: string | null;
  notes: string | null;
}

function toPayload(s: InternalState): PrestationFormValues {
  return {
    clientName: s.clientName.trim(),
    clientEmail: s.clientEmail.trim() || null,
    description: s.description.trim() || null,
    amount: Number(s.amount),
    invoicedAt: s.invoicedAt,
    dueAt: s.dueAt || null,
    notes: s.notes.trim() || null,
  };
}

interface Props {
  initial?: Partial<Prestation>;
  onSubmit: (values: PrestationFormValues) => Promise<void> | void;
  submitLabel?: string;
}

export function PrestationForm({
  initial,
  onSubmit,
  submitLabel = "Enregistrer",
}: Props) {
  const [state, setState] = useState<InternalState>(toState(initial));
  const [submitting, setSubmitting] = useState(false);
  const notesField = useRef<HTMLTextAreaElement>(null);
  const [error, setError] = useState<string | null>(null);

  function update<K extends keyof InternalState>(key: K, value: InternalState[K]) {
    setState((s) => ({ ...s, [key]: value }));
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!state.clientName.trim()) {
      setError("Nom du client requis");
      return;
    }
    const amount = Number(state.amount);
    if (!Number.isFinite(amount) || amount <= 0) {
      setError("Montant invalide");
      return;
    }
    if (!state.invoicedAt) {
      setError("Date de facturation requise");
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      // Valeur réelle du champ : un outil de dictée peut y écrire sans prévenir React.
      await onSubmit(toPayload({ ...state, notes: notesField.current?.value ?? state.notes }));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erreur");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Client" required>
          <input
            className="input-base"
            value={state.clientName}
            onChange={(e) => update("clientName", e.target.value)}
            autoFocus
            required
          />
        </Field>
        <Field label="Email client">
          <input
            type="email"
            className="input-base"
            value={state.clientEmail}
            onChange={(e) => update("clientEmail", e.target.value)}
            placeholder="prenom@exemple.fr"
          />
        </Field>
        <Field label="Description" wide>
          <input
            className="input-base"
            value={state.description}
            onChange={(e) => update("description", e.target.value)}
            placeholder="Ex: GA Access 1 month"
          />
        </Field>
        <Field label="Montant (€)" required>
          <input
            type="number"
            min={0}
            step="0.01"
            className="input-base tabular"
            value={state.amount}
            onChange={(e) => update("amount", e.target.value)}
            required
          />
        </Field>
        <Field label="Facturé le" required>
          <input
            type="date"
            className="input-base"
            value={state.invoicedAt}
            onChange={(e) => update("invoicedAt", e.target.value)}
            required
          />
        </Field>
        <Field label="Échéance (facultatif)">
          <input
            type="date"
            className="input-base"
            value={state.dueAt}
            onChange={(e) => update("dueAt", e.target.value)}
          />
        </Field>
      </div>
      <Field label="Notes">
        <DictationTextarea
          ref={notesField}
          className="input-base"
          initialValue={state.notes}
          onValueChange={(v) => update("notes", v)}
          rows={3}
          placeholder="Contexte de la prestation, accords, etc."
        />
      </Field>
      {error && (
        <p className="text-sm text-[var(--color-coral-600)]">{error}</p>
      )}
      <div className="flex items-center justify-end">
        <button type="submit" className="btn btn-primary" disabled={submitting}>
          {submitting ? "Enregistrement…" : submitLabel}
        </button>
      </div>
    </form>
  );
}

function Field({
  label,
  required,
  wide,
  children,
}: {
  label: string;
  required?: boolean;
  wide?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label className={wide ? "block sm:col-span-2" : "block"}>
      <span className="text-xs font-medium text-[var(--color-muted-foreground)] uppercase tracking-wide">
        {label}
        {required && (
          <span className="text-[var(--color-coral-500)] ml-0.5">*</span>
        )}
      </span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}
